import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from "@angular/router";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";
import { Keepalive } from "@ng-idle/keepalive";
import { MasterService } from "../app/modules/core/services/master.service";
import { StorageHelperService } from "../app/modules/core/services/storage-helper.service";
import { JwtHelperService } from "@auth0/angular-jwt";
import { API_END_POINTS } from "../app/modules/core/constants/app.apiEndPoints";
import {
  ConfirmEventType,
  ConfirmationService,
  MessageService,
  PrimeNGConfig,
} from "primeng/api";

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
    idleState = "Not started.";
    timedOut = false;
    lastPing?: Date = new Date();
    title = "angular-idle-timeout";
    private tokenRefreshInterval: any;
    constructor(
      private primengConfig: PrimeNGConfig,
      private idle: Idle,
      private keepalive: Keepalive,
      private router: Router,
      private confirmationService: ConfirmationService,
      private messageService: MessageService,
      private masterService: MasterService,
      private storageService: StorageHelperService,
    ) {
      // Session state used to live in localStorage, which survived a tab close.
      // Purge anything left behind there by an older build so stale tokens
      // don't sit in the browser forever.
      this.storageService.clearLegacyLocalSession();

      this.router.events.subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          if (event.url !== "" && event.url !== "/login" && event.url !== "/") {
            this.watchTimer(this.idle, this.keepalive);
            this.startTokenRefreshTimer();
          } else if (event.url === "/login" || event.url === "/") {
            this.idle.stop();
            this.confirmationService.close();
            this.stopTokenRefreshTimer();
          }
        }
      });
    }
  
    ngOnInit(): void {
      this.primengConfig.ripple = true;
      // this.confirm1();
    }

    ngOnDestroy(): void {
      this.stopTokenRefreshTimer();
      this.idle.stop();
    }

    startTokenRefreshTimer(): void {
      if (this.tokenRefreshInterval) {
        clearInterval(this.tokenRefreshInterval);
      }
      // Refresh token every 20 minutes or before expiration
      this.tokenRefreshInterval = setInterval(() => {
        const accessToken = this.storageService.getItemFromSession('accessToken');
        if (accessToken) {
          const helper = new JwtHelperService();
          const expirationDate = helper.getTokenExpirationDate(accessToken);
          const now = new Date();

          if (expirationDate) {
            const timeUntilExpiry = expirationDate.getTime() - now.getTime();
            // Refresh if token expires in less than 5 minutes (300000ms)
            if (timeUntilExpiry < 300000 && timeUntilExpiry > 0) {
              this.proactiveRefreshToken();
            }
          }
        }
      }, 60000); // Check every minute
    }

    stopTokenRefreshTimer(): void {
      if (this.tokenRefreshInterval) {
        clearInterval(this.tokenRefreshInterval);
        this.tokenRefreshInterval = null;
      }
    }

    proactiveRefreshToken(): void {
      const accessToken = this.storageService.getItemFromSession('accessToken');
      const refreshToken = this.storageService.getItemFromSession('refreshToken');

      if (accessToken && refreshToken) {
        this.refreshToken().subscribe({
          next: (response: any) => {
            this.storageService.setItemToSession('accessToken', response.token.accessToken);
            this.storageService.setItemToSession('refreshToken', response.token.refreshToken);
            this.storageService.setItemToSession('refreshTokenExpiry', response.token.refreshTokenExpiry);
            const helper = new JwtHelperService();
            const decodedToken = helper.decodeToken(response.token.accessToken);
            sessionStorage.setItem('Access', JSON.stringify(decodedToken.applicationclaims));
          },
          error: (err) => {
            console.error('Token refresh failed:', err);
            this.logout();
          },
        });
      }
    }
  
    watchTimer(idle: any, keepalive: any) {
      // sets an idle timeout of 5 seconds, for testing purposes.
      idle.setIdle(1800); // 30 mins
      // sets a timeout period of 5 seconds. after 10 seconds of inactivity, the user will be considered timed out.
      idle.setTimeout(300);
      // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
      idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
  
      idle.onIdleEnd.subscribe(() => {
        this.idleState = "No longer idle.";
        this.reset();
      });
  
      idle.onTimeout.subscribe(() => {
        this.idleState = "Timed out!";
        this.timedOut = true;
        this.logout();
      });
  
      idle.onIdleStart.subscribe(() => {
        this.idleState = "You've gone idle!";
      });
  
      idle.onTimeoutWarning.subscribe((countdown: any) => {
        this.idleState = "You will be logged out in " + countdown + " seconds!";
        this.confirm(this.idleState);
      });
  
      // sets the ping interval to 15 seconds
      keepalive.interval(15);
  
      keepalive.onPing.subscribe(() => (this.lastPing = new Date()));
  
      this.reset();
    }
  
  
  
  
    reset() {
      this.idle.watch();
      this.idleState = "Started.";
      this.timedOut = false;
    }
  
    refreshToken() {
      const url = this.masterService.constructApiUrl(API_END_POINTS.refreshToken);
      const data = {
        accessToken: this.storageService.getItemFromSession('accessToken') ?? '',
        refreshToken: this.storageService.getItemFromSession('refreshToken') ?? '',
      }
      return this.masterService.post(
        url, data, undefined,
        {
          loader: false,
        }
      );
    }
  
    stay() {
      this.reset();
    }
  
    public logout() {
      this.idle.stop();
      this.confirmationService.close();
      this.router.navigate([""]);
    }
  
    confirm(state: string) {
      this.confirmationService.confirm({
        header: "You have been idle...",
        message: state,
        icon: "pi pi-exclamation-triangle",
        accept: () => {
          this.reset();
          // added on 01042025
          console.log("stay.......");
          this.refreshToken().subscribe({
            next: (response: any) => {
              console.log("refresh tok", response);
              this.storageService.setItemToSession(
                "accessToken",
                response.token.accessToken
              );
              this.storageService.setItemToSession(
                "refreshToken",
                response.token.refreshToken
              );
              this.storageService.setItemToSession(
                "refreshTokenExpiry",
                response.token.refreshTokenExpiry
              );
              const helper = new JwtHelperService();
  
              const decodedToken = helper.decodeToken(
                response.token.accessToken
              );
  
              // Other functions
              const expirationDate = helper.getTokenExpirationDate(
                response.token.accessToken
              );
              const isExpired = helper.isTokenExpired(
                response.token.accessToken
              );
              console.log(decodedToken, expirationDate, isExpired);
              sessionStorage.setItem('Access', JSON.stringify(decodedToken.applicationclaims));
            },
            error: (err) => { console.log("refresh tok err", err); },
          });
          this.messageService.add({
            severity: "info",
            summary: "Stay Back",
            detail: "Thank you for staying back...!",
          });
        },
        reject: (type: ConfirmEventType) => {
          switch (type) {
            case ConfirmEventType.REJECT:
              this.logout();
              this.messageService.add({
                severity: "error",
                summary: "Logout",
                detail: "You have Logged out. Please Login again...!",
              });
              break;
            case ConfirmEventType.CANCEL:
              this.reset();
              this.messageService.add({
                severity: "warn",
                summary: "Cancelled",
                detail: "You have cancelled",
              });
              break;
          }
        },
      });
    }
  
    confirm1() {
      this.confirmationService.confirm({
        message: "No Venues assigned to you. Please contact admin.",
        icon: "pi pi-exclamation-triangle",
        accept: () => { },
        reject: () => { },
      });
    }
  }
  